import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** If set, the user must type this string exactly to enable confirm. */
  typeToConfirm?: string;
  /** Tone of the confirm button. */
  tone?: "danger" | "default";
  onConfirm: () => void | Promise<void>;
  busy?: boolean;
  children?: React.ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  typeToConfirm,
  tone = "default",
  onConfirm,
  busy,
  children,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  const valid = !typeToConfirm || typed === typeToConfirm;

  // Clear the confirmation text whenever the dialog closes, however it closes
  // (Cancel, success, Escape), so a reopened destructive dialog isn't pre-armed.
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setTyped("");
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <div className="mb-1 flex items-center gap-2">
            {tone === "danger" && (
              <span className="grid h-7 w-7 place-items-center rounded-full bg-rose-500/10 text-rose-300">
                <AlertTriangle className="h-4 w-4" />
              </span>
            )}
            <DialogTitle>{title}</DialogTitle>
          </div>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {typeToConfirm && (
          <div className="space-y-1.5">
            <Label htmlFor="confirm-type" className="text-xs text-muted-foreground">
              Type <span className="font-mono text-foreground">{typeToConfirm}</span> to confirm
            </Label>
            <Input
              id="confirm-type"
              autoComplete="off"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="font-mono"
            />
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setTyped("");
              onOpenChange(false);
            }}
            disabled={busy}
          >
            {cancelText}
          </Button>
          <Button
            variant={tone === "danger" ? "destructive" : "default"}
            disabled={!valid || busy}
            onClick={() => onConfirm()}
          >
            {busy ? "Working..." : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
